import React, { useMemo } from 'react';
import { formatSeconds } from '../state/timelineModel';

/**
 * The time ruler, and the surface you scrub on.
 *
 * Tick spacing is chosen in *pixels*, not seconds, so the ruler stays readable
 * at every zoom level — the old fixed 1/5/10/30s ladder either crowded the
 * labels together or left one tick on screen, depending on how long the edit
 * happened to be.
 */

const NICE_STEPS = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
const TARGET_TICK_PX = 90;

function Ruler({ pixelsPerSecond, contentWidth, playhead, totalDuration, onScrub }) {
    const step = useMemo(() => (
        NICE_STEPS.find(candidate => candidate * pixelsPerSecond >= TARGET_TICK_PX)
        || NICE_STEPS[NICE_STEPS.length - 1]
    ), [pixelsPerSecond]);

    const ticks = useMemo(() => {
        const out = [];
        const lastSecond = contentWidth / pixelsPerSecond;
        for (let time = 0; time <= lastSecond; time += step) {
            out.push({ time, x: time * pixelsPerSecond });
        }
        return out;
    }, [step, pixelsPerSecond, contentWidth]);

    // Pointer capture, so a scrub that leaves the ruler vertically keeps
    // scrubbing instead of stopping the moment the cursor crosses a track.
    const beginScrub = (event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const rect = event.currentTarget.getBoundingClientRect();
        onScrub((event.clientX - rect.left) / pixelsPerSecond, { seeking: true });
    };

    const continueScrub = (event) => {
        if (event.buttons !== 1) return;
        const rect = event.currentTarget.getBoundingClientRect();
        onScrub((event.clientX - rect.left) / pixelsPerSecond, { seeking: true });
    };

    return (
        <div
            className="nle-ruler"
            style={{ width: contentWidth }}
            onPointerDown={beginScrub}
            onPointerMove={continueScrub}
            onPointerUp={(event) => event.currentTarget.releasePointerCapture?.(event.pointerId)}
            role="slider"
            aria-label="Timeline position"
            aria-valuemin={0}
            aria-valuemax={Math.max(0, totalDuration)}
            aria-valuenow={Math.min(playhead, Math.max(0, totalDuration))}
            aria-valuetext={formatSeconds(playhead)}
            tabIndex={-1}
        >
            {ticks.map(({ time, x }) => (
                <div key={time} className="nle-ruler-tick" style={{ left: x }}>
                    <span className="nle-ruler-label">{formatSeconds(time)}</span>
                </div>
            ))}
        </div>
    );
}

export default React.memo(Ruler);
